
import { Crosshair, Tooltip } from '@unovis/ts'
import { defineComponent } from 'vue'
import { XYRecord } from '~/utils/charts/unovis'
import { selectCrosshairIndices } from '~/utils/charts/zchart'
import { buildBarSeriesComponents } from './barSeries'
import { buildLineSeriesComponents } from './lineSeries'
import ZChartBaseMixin from './zChartBaseMixin'

export default defineComponent({
  name: 'ZChartAxisMixed',
  mixins: [ZChartBaseMixin],
  props: {
    barOptions: {
      type: Object,
      default: () => ({
        barPadding: 0.5,
        stacked: 0
      })
    }
  },
  computed: {
    chartSignature(): string {
      return JSON.stringify({ ...this.baseSignatureData, barOptions: this.barOptions })
    }
  },
  methods: {
    buildChartComponents(x: (d: XYRecord) => number) {
      return [...this.createBarComponents(x), ...this.createLineComponents(x)]
    },
    createBarComponents(x: (d: XYRecord) => number) {
      return buildBarSeriesComponents({
        chartType: 'axis-mixed',
        dataSets: this.dataSets,
        palette: this.palette,
        x,
        barOptions: this.barOptions as { barPadding?: number; stacked?: number | boolean }
      })
    },
    createLineComponents(x: (d: XYRecord) => number) {
      return buildLineSeriesComponents({
        chartType: 'axis-mixed',
        dataSets: this.dataSets,
        palette: this.palette,
        xyRecords: this.xyRecords,
        x,
        chartId: this.chartId
      })
    },
    createCrosshair(tooltip?: Tooltip | null) {
      const stacked = Boolean((this.barOptions as Record<string, unknown>)?.stacked)
      const { dataIndices, useStacked } = selectCrosshairIndices({
        chartType: 'axis-mixed',
        dataSets: this.dataSets,
        stacked
      })
      const yAccessors = dataIndices.map(
        (index) => (d: XYRecord) => (typeof d.values[index] === 'number' ? d.values[index] : 0)
      )
      const yStackedAccessors = useStacked
        ? dataIndices.map(
            (index) => (d: XYRecord) => (typeof d.values[index] === 'number' ? d.values[index] : 0)
          )
        : undefined
      const crosshairColors = dataIndices.map((index) => this.palette[index] || this.palette[0])

      return new Crosshair<XYRecord>({
        x: (d: XYRecord) => d.x,
        ...(useStacked ? { yStacked: yStackedAccessors } : { y: yAccessors }),
        snapToData: true,
        hideWhenFarFromPointer: false,
        color: crosshairColors,
        strokeColor: crosshairColors,
        strokeWidth: 2,
        tooltip: tooltip ?? undefined,
        template: (datum: XYRecord) => this.createCrosshairTemplate(datum)
      })
    },
    afterChartUpdate() {
      this.ensureLineAreaGradients('axis-mixed')
    },
    afterContainerCreated() {
      this.ensureLineAreaGradients('axis-mixed')
    }
  }
})
